// AddFanDialog.tsx
import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Divider,
  VStack,
  HStack,
  FormControl,
  Button,
  Text,
  Box,
  useToast,
} from "@chakra-ui/react";
import { useForm, Controller, FormProvider } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import KYDInput from "src/ui/KYDInput";
import actions from "src/actions";

import { KYDDatePicker, KYDMultiSelect, KYDSelect } from "src/ui";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { KYDGender } from "common/interfaces/KYDUser";
import { KYDContact } from "@common/interfaces/KYDCampaign";

type TagOption = { label: string; value: string };

export interface AddFanFormValues {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  dob?: string;
  tags: TagOption[] | string[];
  gender?: KYDGender | undefined;
}

const schema = yup.object({
  first_name: yup.string().required("First name is required"),
  last_name: yup.string().required("Last name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phone: yup
    .string()
    .transform((val, orig) => (orig === "" ? null : val))
    .nullable()
    .notRequired(),
  dob: yup
    .string()
    .nullable()
    .notRequired()
    .matches(/^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/, {
      message: "Format: mm/dd/yyyy",
      excludeEmptyString: true,
    }),
  gender: yup
    .string()
    .oneOf(["male", "female", "prefernottosay"], "Invalid gender")
    .transform((val, orig) => (orig === "" ? null : val))
    .optional()
    .nullable()
    .notRequired(),
  tags: yup
    .array()
    .of(
      yup.object({
        label: yup.string().required(),
        value: yup.string().required(),
      })
    )
    .nullable()
    .notRequired(),
});

const AddFanDialog: React.FC<{
  title?: string;
  isOpen: boolean;
  onCreate: (contact: KYDContact) => void;
  onClose: () => void;
}> = ({ title = "Create new contact", onCreate, onClose, isOpen }) => {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [tags, setTags] = useState<{ label: string; value: string }[]>([]);

  const fetchOrganizationTags = useCallback(async () => {
    const res = await actions.ADMIN.fetchOrganizationTags();
    setTags(res.map((tag) => ({ label: tag, value: tag })));
  }, []);

  useEffect(() => {
    fetchOrganizationTags();
  }, [fetchOrganizationTags]);

  const form = useForm<AddFanFormValues>({
    resolver: yupResolver(schema) as any,
    mode: "onChange",
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      dob: "",
      gender: undefined,
      tags: [],
    },
  });

  const {
    handleSubmit,
    control,
    register,
    watch,
    formState: { isDirty },
  } = form;

  function isValidCheck(values: {
    first_name?: string;
    last_name?: string;
    email?: string;
  }): boolean {
    const { first_name = "", last_name = "", email = "" } = values;
    const emailRegex = /^\S+@\S+\.\S+$/;

    return (
      first_name.trim().length > 0 &&
      last_name.trim().length > 0 &&
      emailRegex.test(email.trim())
    );
  }

  const [first_name, last_name, email] = watch([
    "first_name",
    "last_name",
    "email",
  ]);

  const isValid = isValidCheck({ first_name, last_name, email });

  const _onSubmit = async (data: AddFanFormValues) => {
    try {
      setSubmitting(true);
      const res = await actions.ADMIN.createFan({
        ...data,
        tags: data.tags.map((t) => t.value),
      });
      onCreate(res);
      toast({
        title: "Fan created",
        description: "Fan created successfully",
        status: "success",
      });
      onClose();
      form.reset();
    } catch (error) {
      toast({
        title: "Error creating fan",
        description: error.message,
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent bg="white" borderRadius="lg" maxW="600px" w="100%" p={0}>
        <FormProvider {...form}>
          <Box p={6}>
            <ModalHeader p={0} mb={4} fontSize="2xl">
              <HStack justify="space-between">
                <Text fontWeight="400" fontSize="24px">
                  {title}
                </Text>
                <HStack spacing={3}>
                  <Button
                    variant="outline"
                    onClick={onClose}
                    rounded="full"
                    fontSize="13px"
                  >
                    Cancel
                  </Button>
                  <Button
                    colorScheme="green"
                    onClick={handleSubmit(_onSubmit)}
                    isDisabled={!isDirty || !isValid || submitting}
                    rounded="full"
                    fontSize="13px"
                    isLoading={submitting}
                  >
                    Create
                  </Button>
                </HStack>
              </HStack>
            </ModalHeader>

            <Divider mb={6} />

            <ModalBody p={0}>
              <VStack spacing={4} align="stretch">
                <Controller
                  name="first_name"
                  control={control}
                  render={({ field }) => (
                    <KYDInput label="First Name" placeholder="" {...field} />
                  )}
                />
                <Controller
                  name="last_name"
                  control={control}
                  render={({ field }) => (
                    <KYDInput label="Last Name" placeholder="" {...field} />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <KYDInput label="Email" placeholder="" {...field} />
                  )}
                />

                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <KYDInput label="Phone" placeholder="" {...field} />
                  )}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Controller
                    name="dob"
                    control={control}
                    render={({ field }) => (
                      <KYDDatePicker
                        value={field.value}
                        onChange={(dayjs) => {
                          if (dayjs) {
                            field.onChange(dayjs.format("MM/DD/YYYY"));
                          } else {
                            field.onChange("");
                          }
                        }}
                        name="dob"
                      />
                    )}
                  />
                </LocalizationProvider>

                <FormControl>
                  <KYDSelect
                    options={[
                      { label: "Male", value: "male" },
                      { label: "Female", value: "female" },
                      { label: "Prefer not to say", value: "prefernottosay" },
                    ]}
                    placeholder="Gender (optional)"
                    borderRadius="md"
                    fontSize="14px"
                    color="#00000080"
                    {...register("gender")}
                  />
                </FormControl>

                {/* Tag Multi-Select */}
                <FormControl>
                  <Controller
                    name="tags"
                    control={control}
                    render={({ field }) => (
                      <KYDMultiSelect
                        createable
                        values={field.value as TagOption[]}
                        onChange={(vals) => field.onChange(vals)}
                        options={tags}
                        placeholder="Tags"
                        height="24px"
                        minHeight="24px"
                      />
                    )}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
          </Box>

          <Divider />
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};

export default AddFanDialog;
